import { Box, Button, Grid, TextField, useTheme } from '@mui/material';
import { KeyboardEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMe, postLogin, postLogout } from '../../stores/userInfo/slice';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../stores/store';
import { API_CODE, ERROR_CODE } from '../../_constants/apiConst';
import { isFulfilled } from '@reduxjs/toolkit';
import { getSessionStorageRedirectUrl } from '../../_utils/utils';
import { INSPEX_PATHNAME, SESSION_STORAGE_KEY } from '../../_constants/Const';
import { isGetUserInfo } from '../../_utils/typeGuardUtils';
import i18n from '../../i18n';
import { CustomError } from '../../services/CustomError';
import { useNavigate } from 'react-router-dom';
import useErrorModal from '../../hooks/useErrorModal';

const Login: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const { getApiErrorMessage, getTypeErrorMessage } = useErrorModal();

  useEffect(() => {
    (async () => {
      // ログアウトしていない遷移を防ぐ
      const response = await dispatch(getMe());
      if (isFulfilled(response) && isGetUserInfo(response.payload)) {
        navigate(INSPEX_PATHNAME.RMA_SEARCH);
      }
    })();
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      executeLogin();
    }
  };

  const executeLogin = async () => {
    setErrorMsg('');

    // 入力チェック
    if (userId === '' && password === '') {
      return;
    }
    if (userId === '') {
      setErrorMsg(t('ERR_UNFILLED_USER_ID'));
      return;
    }
    if (password === '') {
      setErrorMsg(t('ERR_UNFILLED_PASSWORD'));
      return;
    }

    // 認証済みかチェック
    const getMeResult = await dispatch(getMe());
    if (isFulfilled(getMeResult)) {
      const redirectUrl = getSessionStorageRedirectUrl();
      navigate(redirectUrl);
      sessionStorage.removeItem(SESSION_STORAGE_KEY.REDIRECT_URL);
      return;
    } else {
      if (getMeResult.payload instanceof CustomError) {
        if (
          getMeResult.payload.message !== ERROR_CODE.UNAUTHORIZED.toString()
        ) {
          setErrorMsg(getApiErrorMessage(getMeResult.payload, API_CODE.AUTH));
          return;
        }
      } else {
        setErrorMsg(getApiErrorMessage(getMeResult.payload, API_CODE.AUTH));
        return;
      }
    }

    // ログイン実行
    const loginResult = await dispatch(postLogin({ userId, password }));
    if (isFulfilled(loginResult)) {
      // ユーザ情報のデータが不正な場合、サーバ側との整合性を保つためログアウト実行
      if (!isGetUserInfo(loginResult.payload)) {
        await dispatch(postLogout());
        setErrorMsg(getTypeErrorMessage(API_CODE.LOGIN));
        return;
      }
      // 成功時
      const redirectUrl = getSessionStorageRedirectUrl();
      navigate(redirectUrl);
      sessionStorage.removeItem(SESSION_STORAGE_KEY.REDIRECT_URL);
      // 言語設定の反映
      i18n.changeLanguage(loginResult.payload.language);
      return;
    }
    // 失敗時
    setErrorMsg(getApiErrorMessage(loginResult.payload, API_CODE.LOGIN));
  };

  return (
    <Box flex="0 1 auto">
      <Grid container sx={{ marginTop: '10rem' }}>
        <Grid size={3} />
        <Grid size={6} textAlign={'center'}>
          <Box
            sx={{
              marginTop: '2rem',
              height: '2rem',
              pointerEvents: 'none',
              scale: 1.5,
              backgroundColor: 'black',
              maskImage: `url(/logo/logo.svg)`,
              maskRepeat: 'no-repeat',
              maskPosition: 'center',
            }}
          />
          <Grid fontSize={'1rem'} height={'1.5rem'} color="red" my={'1.5rem'}>
            {errorMsg}
          </Grid>
          <Grid container sx={{ marginBottom: '2rem' }}>
            <Grid size={4}></Grid>
            <Grid size={4} textAlign={'center'}>
              <TextField
                fullWidth
                label={t('LBL_USER_ID')}
                type="text"
                size="small"
                slotProps={{
                  input: {
                    style: {
                      height: '100%',
                      padding: '0px',
                      background: theme.palette.background.default,
                    },
                  },
                }}
                onChange={(event) => setUserId(event.currentTarget.value)}
                onKeyDown={(event) => handleKeyDown(event)}
              />
            </Grid>
          </Grid>

          <Grid container sx={{ marginBottom: '3rem' }}>
            <Grid size={4}></Grid>
            <Grid size={4} textAlign={'center'}>
              <TextField
                autoComplete="off"
                fullWidth
                label={t('LBL_PASSWORD')}
                type="password"
                size="small"
                slotProps={{
                  input: {
                    style: {
                      height: '100%',
                      padding: '0px',
                      background: theme.palette.background.default,
                    },
                  },
                }}
                onChange={(event) => setPassword(event.currentTarget.value)}
                onKeyDown={(event) => handleKeyDown(event)}
              />
            </Grid>
          </Grid>

          <Grid container alignItems={'center'} sx={{ marginBottom: '3rem' }}>
            <Grid size={4} />
            <Grid size={4}>
              <Button
                variant="contained"
                fullWidth
                sx={{ height: '2.5rem' }}
                onClick={() => executeLogin()}
                disabled={userId === '' || password === ''}
              >
                {t('BTN_LOGIN')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={3} />
      </Grid>
    </Box>
  );
};

export default Login;
